import React, { useState, useEffect, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  Card,
  Descriptions,
  Space,
  Typography,
  Tag,
  List,
  Image,
  Row,
  Col,
  Popconfirm,
  Spin,
  message,
  Input
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  FileImageOutlined,
  SearchOutlined,
  CaretUpOutlined,
  CaretDownOutlined
} from "@ant-design/icons";
import { selectRole } from "modules/auth/store/authSlice";
import { UserRole } from "configs/constants";
import { selectAllPackageTypes } from "modules/client/package-types/store/slice";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import warehouseApi from "../api/warehouseApi";
import "./InventoryItemDetailPage.scss";

const { Title, Text } = Typography;

const InventoryItemDetailPage = () => {
  const { inventoryId, itemId } = useParams();
  const warehouseId = inventoryId;
  const history = useHistory();
  const dispatch = useDispatch();
  const role = useSelector(selectRole);
  const packageTypes = useSelector(selectAllPackageTypes);

  const [item, setItem] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [measurementUnit, setMeasurementUnit] = useState("in");
  const [serialSearch, setSerialSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });

  const locations = [
    { id: "loc-1", name: "Main Warehouse" },
    { id: "loc-2", name: "Storage Room A" },
    { id: "loc-3", name: "Cold Storage" },
    { id: "loc-4", name: "Shipping Dock" }
  ];

  useEffect(() => {
    loadData();
    dispatch(getPackageTypeList());
  }, [warehouseId, itemId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [itemData, warehouseData, settings] = await Promise.all([
        warehouseApi.getInventoryItem(itemId),
        warehouseApi.getWarehouse(warehouseId),
        warehouseApi.getSettings()
      ]);
      setItem(itemData);
      setWarehouse(warehouseData);
      setMeasurementUnit(settings.measurementUnit || "in");
    } catch (error) {
      console.error("Error loading data:", error);
      message.error("Failed to load item details");
      handleBack();
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    history.push(`/warehouses/${warehouseId}`);
  };

  const handleEdit = () => {
    history.push(`/warehouses/${warehouseId}/edit/${itemId}`);
  };

  const handleDelete = async () => {
    try {
      await warehouseApi.deleteInventoryItem(itemId);
      message.success("Item deleted successfully");
      handleBack();
    } catch (error) {
      console.error("Error deleting item:", error);
      message.error("Failed to delete item");
    }
  };

  const getLocationName = (locationId) => {
    const location = locations.find((loc) => loc.id === locationId);
    return location ? location.name : "Unknown Location";
  };

  // Format date
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedSerials = useMemo(() => {
    if (!item?.serialNumbersList) return [];

    let filtered = [...item.serialNumbersList];

    if (serialSearch) {
      const search = serialSearch.toLowerCase();
      filtered = filtered.filter(
        (serial) =>
          serial.serialNumber?.toLowerCase().includes(search) ||
          serial.itemAttribute?.toLowerCase().includes(search) ||
          serial.po?.toLowerCase().includes(search)
      );
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key] || "";
        const bVal = b[sortConfig.key] || "";
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [item?.serialNumbersList, serialSearch, sortConfig]);

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? (
      <CaretUpOutlined style={{ marginLeft: 4 }} />
    ) : (
      <CaretDownOutlined style={{ marginLeft: 4 }} />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="inventory-item-detail-page loading-container">
        <Spin size="large" />
        <Text>Loading item details...</Text>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="inventory-item-detail-page error-container">
        <Text>Item not found</Text>
      </div>
    );
  }

  // Calculate total value
  const totalValue = (item.quantity || 0) * (item.unitPrice || 0);

  return (
    <div className="inventory-item-detail-page">
      <div className="breadcrumb-section">
        <div className="breadcrumbs">
          <span className="breadcrumb-item" onClick={() => history.push("/warehouses")}>
            WAREHOUSES
          </span>
          <span className="breadcrumb-item" onClick={handleBack}>
            {warehouse?.name?.toUpperCase()} INVENTORY
          </span>
          <span className="breadcrumb-item">ITEM DETAILS</span>
        </div>
      </div>

      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            style={{ marginRight: 16 }}
          >
            Back to Inventory
          </Button>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              {item.name}
            </Title>
            <Text type="secondary">{item.itemId}</Text>
          </div>
        </div>
        <Space>
          {role !== UserRole.VIEWER && (
            <Button type="default" icon={<EditOutlined />} onClick={handleEdit}>
              Edit Item
            </Button>
          )}
          {role !== UserRole.VIEWER && (
            <Popconfirm
              title="Are you sure you want to delete this item?"
              onConfirm={handleDelete}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>
                Delete Item
              </Button>
            </Popconfirm>
          )}
        </Space>
      </div>

      <Row gutter={24}>
        <Col span={16}>
          <Card title="Item Details" className="detail-card">
            <Descriptions column={2} bordered>
              <Descriptions.Item label="Item ID" span={1}>
                <Text strong>{item.itemId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Item Type" span={1}>
                <Tag color={!item.isFungible ? "blue" : "default"}>
                  {!item.isFungible ? "Unique" : "Non-Unique"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Package Type" span={2}>
                {item.packageTypeId ? (
                  <Tag color="blue">
                    {(() => {
                      const pkgType = packageTypes.find(
                        (t) => t.id === item.packageTypeId
                      );
                      if (!pkgType) return "None";
                      const displayLength =
                        measurementUnit === "in" && pkgType.maxLength
                          ? (pkgType.maxLength / 2.54).toFixed(1)
                          : pkgType.maxLength;
                      return (
                        <>
                          {pkgType.name}
                          {(pkgType.maxWeight || pkgType.maxLength) && (
                            <span style={{ marginLeft: "8px", fontSize: "11px" }}>
                              ({pkgType.maxWeight && `${pkgType.maxWeight}kg`}
                              {pkgType.maxWeight && pkgType.maxLength && " · "}
                              {pkgType.maxLength && `${displayLength}${measurementUnit}`})
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </Tag>
                ) : (
                  <Tag color="blue">None</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Name" span={2}>
                <Text strong>{item.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {item.description || "No description provided"}
              </Descriptions.Item>
              <Descriptions.Item label="Location" span={1}>
                {getLocationName(item.location)}
              </Descriptions.Item>
              <Descriptions.Item label="Quantity" span={1}>
                <Text strong>{Math.floor(item.quantity || 0)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Unit Price" span={1}>
                ${parseFloat(item.unitPrice || 0).toFixed(2)}
              </Descriptions.Item>
              <Descriptions.Item label="Total Value" span={1}>
                <Text strong>${totalValue.toFixed(2)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Created" span={1}>
                {formatDate(item.createdAt)}
              </Descriptions.Item>
              {item.updatedAt && (
                <Descriptions.Item label="Last Updated" span={1}>
                  {formatDate(item.updatedAt)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* Serial Numbers */}
          {item.serialNumbersList && item.serialNumbersList.length > 0 && (
            <Card
              title="Serial Numbers - Attributes - PO"
              className="detail-card"
              style={{ marginTop: 24 }}
              extra={
                <Input
                  placeholder="Search serial numbers..."
                  prefix={<SearchOutlined />}
                  value={serialSearch}
                  onChange={(e) => setSerialSearch(e.target.value)}
                  style={{ width: 250 }}
                  allowClear
                />
              }
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f0f0f0" }}>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={() => handleSort("serialNumber")}
                    >
                      Serial Number {getSortIcon("serialNumber")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={() => handleSort("itemAttribute")}
                    >
                      Attribute {getSortIcon("itemAttribute")}
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: 600,
                        cursor: "pointer"
                      }}
                      onClick={() => handleSort("po")}
                    >
                      PO {getSortIcon("po")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedSerials.map((serial, index) => (
                    <tr key={index} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "12px" }}>
                        <Tag color="blue">{serial.serialNumber}</Tag>
                      </td>
                      <td style={{ padding: "12px" }}>
                        {serial.itemAttribute ? (
                          <Tag color="green">{serial.itemAttribute}</Tag>
                        ) : (
                          <Text type="secondary">-</Text>
                        )}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {serial.po || <Text type="secondary">-</Text>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredAndSortedSerials.length === 0 && serialSearch && (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#999" }}>
                  No serial numbers match your search.
                </div>
              )}
            </Card>
          )}

          {/* Options */}
          {item.options && Object.keys(item.options).length > 0 && (
            <Card
              title="Additional Options"
              className="detail-card"
              style={{ marginTop: 24 }}
            >
              <Descriptions column={2} bordered>
                {Object.entries(item.options).map(([key, value]) => (
                  <Descriptions.Item label={key} key={key} span={1}>
                    {value}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </Card>
          )}
        </Col>

        <Col span={8}>
          <Card title="Item Image" className="detail-card">
            {item.image ? (
              <div className="item-image-container">
                <Image
                  src={item.image}
                  alt={item.name}
                  style={{ width: "100%", borderRadius: 8 }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RUG8G+2VdZGzAA3gIMdvgUgRGNvAAJHqOH09q2SJ+uJnW3+Ac+m5CqDGGQjKJUYHACrCcqCQZQA..."
                />
              </div>
            ) : (
              <div className="no-image">
                <FileImageOutlined style={{ fontSize: 48, color: "#ccc" }} />
                <p style={{ color: "#999", marginTop: 16 }}>No image uploaded</p>
              </div>
            )}
          </Card>

          <Card title="Summary" className="detail-card" style={{ marginTop: 24 }}>
            <div className="summary-item">
              <Text type="secondary">Total Items:</Text>
              <Title level={4} style={{ margin: 0, color: "#1f3998" }}>
                {Math.floor(item.quantity || 0)}
              </Title>
            </div>
            <div className="summary-item">
              <Text type="secondary">Total Value:</Text>
              <Title level={4} style={{ margin: 0, color: "#52c41a" }}>
                ${totalValue.toFixed(2)}
              </Title>
            </div>
            <div className="summary-item">
              <Text type="secondary">Serial Numbers:</Text>
              <Title level={4} style={{ margin: 0, color: "#fa8c16" }}>
                {item.serialNumbersList?.length || 0}
              </Title>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InventoryItemDetailPage;
